import { BaseTableExportService } from "@/services/baseTableExportService";
import { downloadFile } from "@repo/shared/utils";
import { Button } from "@repo/ui/components/Base/Button";
import { CheckboxGroupInput } from "@repo/ui/components/Base/CheckboxGroupInput";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { Formik } from "formik";
import { useState } from "react";

export interface ExportFormProps<T> {
  getData: () => T[];
  getPreselectedFields: () => string[];
  onCancel?: () => void;
  exportService: BaseTableExportService<T>;
  downloadName: string;
}

export const ExportForm = <T,>({
  getData,
  getPreselectedFields,
  onCancel,
  exportService,
  downloadName,
}: ExportFormProps<T>) => {
  const getSelectedFields = (): string[] => {
    const preselectedFields = getPreselectedFields();
    const { config } = exportService;

    return Object.keys(config).filter((key) =>
      preselectedFields.includes(config[key].alias || key)
    );
  };

  const [progress, setProgress] = useState<number | null>(null);

  return (
    <Formik
      initialValues={{
        selectedFields: getSelectedFields(),
      }}
      async
      onSubmit={async (values) => {
        const selectedFields = Object.keys(exportService.config).filter((key) =>
          values.selectedFields.includes(key)
        );

        const data = getData();

        const csv = await exportService.getCSV({
          rows: data,
          fields: selectedFields,
          onProgress: setProgress,
        });

        setTimeout(
          // don't clear progress if user started another export process
          () => setProgress((progress) => (progress === 1 ? null : progress)),
          1000
        );

        downloadFile(
          new Blob([csv], { type: "text/csv;charset=utf-8;" }),
          downloadName
        );
      }}
    >
      {({
        values,
        handleSubmit,
        setFieldValue,
        // setTouched,
        // touched,
        isSubmitting,
      }) => {
        const selectAll = () => {
          setFieldValue("selectedFields", Object.keys(exportService.config));
        };
        const selectNone = () => {
          setFieldValue("selectedFields", []);
        };
        const selectInverse = () => {
          setFieldValue(
            "selectedFields",
            Object.keys(exportService.config).filter(
              (key) => !values.selectedFields.includes(key)
            )
          );
        };

        return (
          <form onSubmit={handleSubmit}>
            <DialogBody className="flex flex-col gap-y-4">
              <div className="flex flex-row">
                <div className="flex flex-col gap-8 pb-5">
                  <CheckboxGroupInput
                    disabled={isSubmitting}
                    label="Fields to export:"
                    orientation="vertical"
                    options={Object.keys(exportService.config).map((key) => ({
                      key,
                      label: exportService.config[key].resultFieldname,
                    }))}
                    value={values.selectedFields}
                    onValueChange={(value) => {
                      setFieldValue("selectedFields", value);
                    }}
                  />
                  <div className="flex flex-row gap-2">
                    <Button
                      disabled={isSubmitting}
                      variant="tertiary"
                      className="h-8"
                      onClick={selectAll}
                    >
                      All
                    </Button>
                    <Button
                      disabled={isSubmitting}
                      variant="destructive"
                      className="h-8"
                      onClick={selectNone}
                    >
                      None
                    </Button>
                    <Button
                      disabled={isSubmitting}
                      className="h-8"
                      onClick={selectInverse}
                    >
                      Invert
                    </Button>
                  </div>
                </div>
                <div className="grow pl-10"></div>
              </div>
            </DialogBody>
            <DialogFooter className="flex flex-row gap-4 items-center">
              <Button
                variant="destructive"
                type="button"
                disabled={isSubmitting}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <div className="grow" />
              {progress != null && (
                <p>Progress: {Math.round(progress * 100)}%</p>
              )}
              <Button
                type="submit"
                disabled={isSubmitting || !values.selectedFields.length}
              >
                Export CSV
              </Button>
            </DialogFooter>
          </form>
        );
      }}
    </Formik>
  );
};
